import { useEffect, useState } from 'react';
import { useCurrency } from './currencyContext';
import { DEFAULT_FX_NZD, getFxNzdCached } from '@/services/fxService';

// The currency the dashboard shows money in: a picker (USD or NZD) and, for
// NZD, the exchange rate. Every figure is stored in USD and converted only
// when it is drawn. NZD starts on the day's reference rate; a person can type
// their own.

const PRESETS: Array<{ code: string; prefix: string; defaultRate: number }> = [
  { code: 'USD', prefix: '$', defaultRate: 1 },
  { code: 'NZD', prefix: 'NZ$', defaultRate: DEFAULT_FX_NZD },
];

export function CurrencyControl() {
  const { code, rate, setCurrency } = useCurrency();
  // Local rate buffer so the user can type "1.6" without each keystroke
  // triggering a re-render of every money number in the dashboard. We push
  // to context on blur or Enter.
  const [rateBuffer, setRateBuffer] = useState(String(rate));

  useEffect(() => {
    setRateBuffer(String(rate));
  }, [rate]);

  const commitRate = () => {
    const parsed = Number(rateBuffer);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setRateBuffer(String(rate));
      return;
    }
    if (parsed === rate) return;
    const preset = PRESETS.find((p) => p.code === code);
    setCurrency(code, parsed, preset?.prefix || code + '$');
  };

  const pickCode = (nextCode: string) => {
    const preset = PRESETS.find((p) => p.code === nextCode);
    // NZD picks up the session's live ECB rate (falls back to the default offline).
    const nextRate = nextCode === 'NZD' ? getFxNzdCached().rate : (preset?.defaultRate ?? 1);
    const nextPrefix = preset?.prefix ?? nextCode + '$';
    setCurrency(nextCode, nextRate, nextPrefix);
  };

  return (
    <div className="nd-currency">
      <label className="nd-currency__label text-muted" htmlFor="nd-currency-code">
        Currency
      </label>
      <select
        id="nd-currency-code"
        className="form-select form-select-sm"
        value={code}
        onChange={(e) => pickCode(e.target.value)}
        style={{ width: 75 }}
        title="Every figure is stored in US dollars. NZD shows each one multiplied by the rate beside it."
      >
        {PRESETS.map((p) => (
          <option key={p.code} value={p.code}>
            {p.code}
          </option>
        ))}
      </select>
      {code !== 'USD' && (
        <>
          <input
            type="number"
            step="0.0001"
            min="0.0001"
            className="form-control form-control-sm"
            value={rateBuffer}
            onChange={(e) => setRateBuffer(e.target.value)}
            onBlur={commitRate}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitRate();
                (e.target as HTMLInputElement).blur();
              }
            }}
            style={{ width: 90 }}
            aria-label={`${code} for one US dollar`}
            title={`The exchange rate: how many ${code} one US dollar buys.`}
          />
          <span className="nd-currency__unit text-muted">{code} per USD</span>
        </>
      )}
    </div>
  );
}
